import { useState, useEffect, useMemo } from 'react';
import { Loader2, ChevronsUpDown, Check, Calendar } from 'lucide-react';
import { Card, CardContent } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../../foundation/altzero_base/base/components/ui/command';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { billService } from '../../services/billService';
import type { BvBill } from '../../types/billTypes';

type Props = {
  siteId: string;
  /** Currently open bill run. '' = none selected (site level). */
  currentBillId?: string;
  /** Called when user picks a bill run. */
  onSelect: (billId: string) => void;
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function BillRunChooser({ siteId, currentBillId, onSelect }: Props) {
  const [bills, setBills] = useState<BvBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [comboOpen, setComboOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    billService.listBySite(siteId).then((res) => {
      if (!mounted) return;
      if (res.success) setBills(res.data ?? []);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [siteId]);

  // Group bill runs by year (bill_from year), newest year first.
  const yearGroups = useMemo(() => {
    const map = new Map<number, BvBill[]>();
    for (const b of bills) {
      const y = new Date(b.bill_from).getFullYear();
      if (!Number.isNaN(y)) {
        const arr = map.get(y) ?? [];
        arr.push(b);
        map.set(y, arr);
      }
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [bills]);

  const current = bills.find((b) => b.id === currentBillId) ?? null;

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Bill Run</span>

          <Popover open={comboOpen} onOpenChange={setComboOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboOpen}
                disabled={loading || bills.length === 0}
                className="w-72 h-9 justify-between font-normal"
              >
                {loading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                  </span>
                ) : current ? (
                  <span className="flex items-center gap-2 truncate">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">
                      {fmtDate(current.bill_from)} – {fmtDate(current.bill_to)}
                      {current.billing_days ? ` · ${current.billing_days}d` : ''}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {bills.length === 0 ? 'No bill runs yet' : 'Select a bill run…'}
                  </span>
                )}
                <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search by date or days…" className="h-9 text-xs" />
                <CommandList>
                  <CommandEmpty>No bill runs found.</CommandEmpty>
                  {yearGroups.map(([year, group]) => (
                    <CommandGroup key={year} heading={String(year)}>
                      {group.map((b) => (
                        <CommandItem
                          key={b.id}
                          value={`${fmtDate(b.bill_from)} ${fmtDate(b.bill_to)} ${b.billing_days ?? ''} ${year}`}
                          onSelect={() => { onSelect(b.id); setComboOpen(false); }}
                          className="gap-2"
                        >
                          <Check
                            className={cn('h-3.5 w-3.5', currentBillId === b.id ? 'opacity-100' : 'opacity-0')}
                          />
                          <span className="text-xs">
                            {fmtDate(b.bill_from)} – {fmtDate(b.bill_to)}
                            {b.billing_days ? ` · ${b.billing_days}d` : ''}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {bills.length === 0 && !loading && (
          <p className="text-xs text-muted-foreground">
            No bill runs yet — upload an invoice on the Invoices tab to create one; its period always comes from the invoice.
          </p>
        )}
      </CardContent>
    </Card>
  );
}